/*
 * ==============================================================================
 *
 * Copyright (c) 2023. Koninklijke Philips Electronics N.V.
 *
 * All rights are reserved. Reproduction in whole or in part is prohibited without the prior written consent of the copyright holder.
 *
 * ==============================================================================
 */

import React, { useEffect, useState, useRef } from "react";
import { Loading } from "./AllReports";
import { CSVLink } from "react-csv";
import { deviceType } from "../../utils/helpers";
import { patientEngagementHeaderCsv } from "../../constants/HeaderCsvConstants";
import { useHistory } from "react-router-dom";
import fetchHelper from "../../services/fetchHelper";
import { useToasts } from "react-toast-notifications";
import PatientEngagementModal from "./PatientEngagementModal";
import dayjs from "dayjs";
import Pagination from "../../components/Pagination";
import "./PatientEngagementTable.scss";
import cn from "classnames";
import sortBy from "lodash/sortBy";

const PatientEngagementTable = () => {
  const history = useHistory();
  const { addToast } = useToasts();
  const [isLoading, setIsLoading] = useState(false);
  const [data, setData] = useState([]);
  const [reasons, setReasons] = useState([]);
  const [selectedTask, setSelectedTask] = useState(null);
  const [headers, setHeaders] = useState(patientEngagementHeaderCsv);
  const [offset, setOffset] = useState(0);
  const [to, setTo] = useState(null);
  const view = useRef("OPEN");
  const reason = useRef("");
  const TASKS_PER_PAGE = 30;

  const viewDetail = (id) => {
    localStorage.setItem("master-location", "/reports3/patientengagement?return=true");
    history.push(`/patients3/${id}/notes`);
  };

  useEffect(() => {
    fetchReasons();
  }, []);

  useEffect(() => {
    fetchTasksByFilter();
  }, [offset]);

  const displayError = (error) => {
    if (Array.isArray(error.errors)) {
      error.errors.forEach((err) => {
        addToast(err.msg, {
          appearance: "error",
          autoDismiss: false,
        });
      });
    }
  };

  const fetchReasons = async () => {
    try {
      const data = await fetchHelper.get({
        url: `/patientmanagement/rest/engagement/reasons`,
      });
      setReasons(sortBy(data, ["description"]));
    } catch ({ errors }) {
      console.log(errors);
    }
  };

  const fetchTasksByFilter = async () => {
    setIsLoading(true);
    try {
      const apiUrl = `/patientmanagement/rest/engagement/taskListView?filterType=${view.current}`;
      const data = await fetchHelper.get({
        url: reason.current !== "" ? `${apiUrl}&reasonId=${reason.current}` : apiUrl,
        headers: {
          "X-Range": `items=${offset}-${offset + TASKS_PER_PAGE - 1}`,
          Range: `items=${offset}-${offset + (TASKS_PER_PAGE - 1)}`,
        },
      });
      setData(data);
      setTo(offset + data.length - 1);
    } catch (error) {
      displayError(error);
    } finally {
      setIsLoading(false);
    }
  };

  const getPhoneNumbersFormatted = (engagementItem, isCSVFile = false) => {
    const homeNumber = getFormattedField(engagementItem, "homeNumber");
    const phoneNumber = getFormattedField(engagementItem, "phoneNumber");
    let number = "";

    if (homeNumber) {
      number += homeNumber;
    }
    if (homeNumber && phoneNumber) {
      number += isCSVFile ? ", " : "\n";
    }
    if (phoneNumber) {
      number += phoneNumber;
    }

    return number;
  };

  const getFormattedField = (data, key) => {
    const defaultDateFormat = "MM/DD/YY h:mm:ss a";

    switch (key) {
      case "requested":
        return dayjs.unix(data.taskCreatedDate).format(defaultDateFormat);
      case "name":
        return `${data?.patientLastName}, ${data?.patientFirstName}`;
      case "dob":
        return data.patientDOB ? dayjs(new Date(data.patientDOB)).format("MM/DD/YYYY") : "-";
      case "device":
        return data.deviceManufacturerName || "-";
      case "deviceType":
        return deviceType(data.deviceType) || "-";
      case "phoneNumber":
        return data.phoneNumber || "";
      case "homeNumber":
        return data.homeNumber || "";
      case "assigned":
        return data.taskAssigned && data.taskAssignUserLastName && data.taskAssignUserFirstName
          ? `${data.taskAssignUserLastName}, ${data.taskAssignUserFirstName}`
          : "-";
      case "note":
        return data.taskEndNote || data.taskInitialNote || "-";
      case "reason":
        return data.taskReason || "-";
      case "location":
        return data.locationName || "-";
      case "dateClosed":
        return data.taskClosedDate ? dayjs.unix(data.taskClosedDate).format(defaultDateFormat) : "-";
      case "closedBy":
        return data.taskCloseUserFirstName || data.taskCloseUserLastName
          ? `${data.taskCloseUserLastName}, ${data.taskCloseUserFirstName}`
          : "-";
      case "createdBy":
        return data.creatorUserFirstName || data.creatorUserLastName
          ? `${data.creatorUserLastName}, ${data.creatorUserFirstName}`
          : "-";
    }
  };

  const patientEngagementTableCsv = data.map((item) => ({
    requested: getFormattedField(item, "requested"),
    name: getFormattedField(item, "name"),
    dob: getFormattedField(item, "dob"),
    device: getFormattedField(item, "device"),
    deviceType: getFormattedField(item, "deviceType"),
    phone: getPhoneNumbersFormatted(item, true),
    assigned: getFormattedField(item, "assigned"),
    note: getFormattedField(item, "note"),
    reason: getFormattedField(item, "reason"),
    location: getFormattedField(item, "location"),
    dateClosed: getFormattedField(item, "dateClosed"),
    closedBy: getFormattedField(item, "closedBy"),
    createdBy: getFormattedField(item, "createdBy"),
  }));

  const onChangeFilter = (newFilter, type) => {
    setSelectedTask(null);
    setTo(null);

    if (type === "button") {
      view.current = newFilter;
      if (["COMPLETED", "ALL"].includes(newFilter)) {
        setHeaders(patientEngagementHeaderCsv);
      } else {
        setHeaders(headers.filter((csvHeader) => !["dateClosed", "closedBy"].includes(csvHeader.key)));
      }
    } else {
      reason.current = newFilter;
    }

    if (offset === 0) {
      fetchTasksByFilter();
    } else {
      setOffset(0);
    }
  };

  const onClose = (shouldRefresh) => {
    setSelectedTask(null);
    if (shouldRefresh) fetchTasksByFilter();
  };

  return (
    <>
      {selectedTask && (
        <PatientEngagementModal task={selectedTask} onClose={onClose} refresh={fetchTasksByFilter} />
      )}
      <div id="patient-engagement-table-container">
        <div className="left-container">
          <div>
            <h5>Reason</h5>
            <div className="search-container">
              <select
                value={reason.current}
                onChange={(e) => onChangeFilter(e.target.value, "dropdown")}
                disabled={isLoading}
              >
                <option value=""></option>
                {reasons.map((item, index) => (
                  <option key={index} value={item.id}>
                    {item.description}
                  </option>
                ))}
              </select>
              <button
                className="btn btnTeal"
                disabled={isLoading}
                onClick={() => onChangeFilter("", "dropdown")}
              >
                Clear
              </button>
            </div>
          </div>
          <button
            id="all-open-tasks"
            className={cn({ "btn-yellow": view.current === "OPEN" }, "btn")}
            onClick={() => onChangeFilter("OPEN", "button")}
            disabled={isLoading}
          >
            All Open Tasks
          </button>
          <button
            id="all-reserved-tasks"
            className={cn({ "btn-yellow": view.current === "RESERVED" }, "btn")}
            onClick={() => onChangeFilter("RESERVED", "button")}
            disabled={isLoading}
          >
            All Reserved Tasks
          </button>
          <button
            id="all-completed-tasks"
            className={cn({ "btn-yellow": view.current === "COMPLETED" }, "btn")}
            onClick={() => onChangeFilter("COMPLETED", "button")}
            disabled={isLoading}
          >
            All Completed Tasks
          </button>
          <button
            id="all-available-tasks"
            className={cn({ "btn-yellow": view.current === "ALL" }, "btn")}
            onClick={() => onChangeFilter("ALL", "button")}
            disabled={isLoading}
          >
            All Tasks
          </button>
        </div>
        <div className="right-container">
          <CSVLink
            id="download-csv-btn"
            data={patientEngagementTableCsv}
            headers={headers}
            filename="Patient engagement report.csv"
            className="csv-link-missed-due"
          >
            <button disabled={isLoading || !data.length} className="btn btnTeal hideAdv">
              Download CSV
            </button>
          </CSVLink>
        </div>
      </div>
      <div className="contentTbl">
        <table className="overview" id="patient-engagement-table">
          <thead>
            <tr>
              <th>Date/Time Request Submitted</th>
              <th>Submitted by</th>
              <th>
                <a href="#" id="name-header">
                  Last Name, First Name
                </a>
              </th>
              <th>Date of birth</th>
              <th>
                <a href="#" id="device-manuf-header">
                  Device Manufacturer
                </a>
              </th>
              <th>
                <a href="#" id="device-type">
                  Device Type
                </a>
              </th>
              <th>Phone</th>
              <th>Reserved by</th>
              <th>Last PE Note</th>
              <th>Reason</th>
              <th>
                <a href="#" id="location">
                  Location
                </a>
              </th>
              {["COMPLETED", "ALL"].includes(view.current) && (
                <>
                  <th>Date Closed</th>
                  <th>Closed By</th>
                </>
              )}
              <th></th>
            </tr>
          </thead>
          {isLoading ? (
            <Loading colspan="18" />
          ) : (
            <tbody>
              {data.map((item, index) => (
                <tr key={item.taskId} id={`task-row-${index}`}>
                  <td id={`requested-time-${index}`}>{getFormattedField(item, "requested")}</td>
                  <td data-testid={`created-by-${index}`} id={`created-by-${index}`}>
                    {getFormattedField(item, "createdBy")}
                  </td>
                  <td id={`last-first-name-${index}`}>{getFormattedField(item, "name")}</td>
                  <td id={`dob-${index}`}>{getFormattedField(item, "dob")}</td>
                  <td id={`-${index}`}>{getFormattedField(item, "device")}</td>
                  <td id={`device-type-${index}`}>{getFormattedField(item, "deviceType")}</td>
                  <td id={`phone-${index}`}>{getPhoneNumbersFormatted(item)}</td>
                  <td id={`assigned-to-${index}`}>{getFormattedField(item, "assigned")}</td>
                  <td id={`note-${index}`} className="allow-line-breaks" style={{ width: "20%" }}>
                    {getFormattedField(item, "note")}
                  </td>
                  <td id={`reason-${index}`}>{getFormattedField(item, "reason")}</td>
                  <td id={`location-${index}`}>{getFormattedField(item, "location")}</td>
                  {["COMPLETED", "ALL"].includes(view.current) && (
                    <>
                      <td id={`date-closed-${index}`}>{getFormattedField(item, "dateClosed")}</td>
                      <td id={`close-by-${index}`}>{getFormattedField(item, "closedBy")}</td>
                    </>
                  )}
                  <td>
                    <div id={`view-btn-${index}`} className="show-options" style={{ width: "450px" }}>
                      <a
                        id={`view-edit-btn-${index}`}
                        onClick={() => viewDetail(item?.patientId)}
                        className="btn btnTeal"
                      >
                        View/Edit
                      </a>
                      {item.taskStatus === "OPEN" && (
                        <a
                          id={`open-task-btn-${index}`}
                          onClick={() => setSelectedTask(item)}
                          className="btn btnTeal"
                        >
                          Open Task
                        </a>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          )}
        </table>
        {!isLoading && !data.length && (
          <div
            id="no-reports"
            style={{
              width: "100%",
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              height: "300px",
              fontSize: "18px",
            }}
          >
            No reports
          </div>
        )}
      </div>
      <Pagination
        offset={offset}
        to={to}
        limit={TASKS_PER_PAGE}
        onSetOffset={setOffset}
        loading={isLoading}
      />
    </>
  );
};

export default PatientEngagementTable;
